import React, { useState, useEffect } from 'react';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Message,
  Modal,
  Tab,
  Table,
  Label,
  Dropdown,
  Segment,
} from 'semantic-ui-react';
import { showError, showSuccess, verifyJSON } from '../helpers';
import TimePricingDetails from './TimePricingDetails';
import ImageTokenPricingDetails, { ImageTokenPricingEditor, buildImageTokenPricing } from './ImageTokenPricing';
import TimePricingEditor, { validateTimePricing } from './TimePricingEditor';
import CacheCreationPricingEditor, { buildCacheCreationPrices } from './CacheCreationPricingEditor';

const PricingEditor = ({ config, onConfigChange, stats }) => {
  const [jsonConfig, setJsonConfig] = useState('');
  const [parsedConfig, setParsedConfig] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedModelType, setSelectedModelType] = useState('token_models');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingModel, setEditingModel] = useState(null);

  // 清理价格对象，移除值为0或undefined的字段
  const cleanPriceObject = (priceObj, preserveZero = false) => {
    if (!priceObj || typeof priceObj !== 'object') return undefined;
    const cleaned = {};
    if (
      priceObj.usd != null &&
      priceObj.usd !== '' &&
      (preserveZero || priceObj.usd > 0)
    )
      cleaned.usd = priceObj.usd;
    if (
      priceObj.cny != null &&
      priceObj.cny !== '' &&
      (preserveZero || priceObj.cny > 0)
    )
      cleaned.cny = priceObj.cny;
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  };

  const isCreditModelType = selectedModelType === 'credit_models';
  const isUpscaleModelType = selectedModelType === 'upscale_models';

  const parseCreditPlans = () => {
    const text = (newModel.plans_json || '').trim();
    if (!text) return undefined;
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('plans 必须是 JSON 对象');
    }
    return parsed;
  };

  const buildCreditModelConfig = () => {
    const provider = newModel.model.trim();
    if (!provider) {
      throw new Error('请填写 Provider');
    }
    const config = {
      provider,
      enabled: newModel.enabled,
    };
    if (newModel.description.trim()) {
      config.description = newModel.description.trim();
    }
    if (newModel.vendor.trim()) {
      config.vendor = newModel.vendor.trim();
    }
    if (newModel.url.trim()) {
      config.url = newModel.url.trim();
    }
    const creditPrice = cleanPriceObject(newModel.credit_price);
    if (creditPrice) {
      config.credit_price = creditPrice;
    }
    const plans = parseCreditPlans();
    if (plans) {
      config.plans = plans;
    }
    if (newModel.active_plan.trim()) {
      config.active_plan = newModel.active_plan.trim();
    }
    return config;
  };

  const buildUpscaleModelConfig = () => {
    const model = newModel.model.trim();
    if (!model) {
      throw new Error('请填写模型名称');
    }
    if (!newModel.vendor.trim()) {
      throw new Error('请填写供应商 (vendor)');
    }
    if (!newModel.category.trim()) {
      throw new Error('请填写类别 (category)');
    }
    const config = {
      model,
      type: 'credit',
      enabled: newModel.enabled,
      description: newModel.description,
      vendor: newModel.vendor.trim(),
      category: newModel.category.trim(),
    };
    if (newModel.url.trim()) {
      config.url = newModel.url.trim();
    }
    if (newModel.credit_provider.trim()) {
      config.credit_provider = newModel.credit_provider.trim();
    }
    return config;
  };

  // 清理条件规则，移除空的价格字段
  const cleanConditionalRules = (rules) => {
    if (!Array.isArray(rules)) return [];
    return rules.map((rule) => {
      const cleanedRule = { ...rule };
      const preserveZero = !!(rule.cache_creation_prices || newModel.cache_creation_prices);
      const cachePrices = buildCacheCreationPrices(rule.cache_creation_prices);
      if (cachePrices) cleanedRule.cache_creation_prices = cachePrices;
      else delete cleanedRule.cache_creation_prices;
      const cleanedInputPrice = cleanPriceObject(rule.input_price, preserveZero);
      const cleanedOutputPrice = cleanPriceObject(rule.output_price, preserveZero);
      const cleanedCacheCreationPrice = cleanPriceObject(
        rule.cache_creation_price
      );
      const cleanedCacheReadPrice = cleanPriceObject(rule.cache_read_price, preserveZero);

      if (cleanedInputPrice) {
        cleanedRule.input_price = cleanedInputPrice;
      } else {
        delete cleanedRule.input_price;
      }

      if (cleanedOutputPrice) {
        cleanedRule.output_price = cleanedOutputPrice;
      } else {
        delete cleanedRule.output_price;
      }

      if (cleanedCacheCreationPrice) {
        cleanedRule.cache_creation_price = cleanedCacheCreationPrice;
      } else {
        delete cleanedRule.cache_creation_price;
      }

      if (cleanedCacheReadPrice) {
        cleanedRule.cache_read_price = cleanedCacheReadPrice;
      } else {
        delete cleanedRule.cache_read_price;
      }

      return cleanedRule;
    });
  };

  const normalizeContextBoolean = (value) => {
    if (value === true || value === false) return value;
    return undefined;
  };

  const cleanContextRules = (rules) => {
    if (!Array.isArray(rules)) return [];
    return rules
      .map((rule) => {
        const cleanedRule = { ...rule };
        const when = {};
        const hasReferenceVideo = normalizeContextBoolean(
          rule?.when?.has_reference_video
        );
        const hasAudio = normalizeContextBoolean(rule?.when?.has_audio);
        if (hasReferenceVideo !== undefined)
          when.has_reference_video = hasReferenceVideo;
        if (hasAudio !== undefined) when.has_audio = hasAudio;
        const resolution = rule?.when?.resolution?.trim?.();
        if (resolution) when.resolution = resolution;

        const override = {};
        const cleanedInputPrice = cleanPriceObject(rule?.override?.input_price);
        const cleanedOutputPrice = cleanPriceObject(
          rule?.override?.output_price
        );
        if (cleanedInputPrice) override.input_price = cleanedInputPrice;
        if (cleanedOutputPrice) override.output_price = cleanedOutputPrice;

        cleanedRule.when = when;
        cleanedRule.override = override;
        if (!cleanedRule.description) delete cleanedRule.description;
        return cleanedRule;
      })
      .filter(
        (rule) =>
          Object.keys(rule.when).length > 0 &&
          Object.keys(rule.override).length > 0
      );
  };

  // 新模型表单
  const [newModel, setNewModel] = useState({
    model: '',
    type: 'simple',
    enabled: true,
    description: '',
    category: '',
    vendor: '',
    url: '',
    // simple
    input_price: { usd: 0, cny: 0 },
    output_price: { usd: 0, cny: 0 },
    // conditional
    conditional_rules: [],
    context_rules: [],
    // matrix
    price_matrix: {},
    // multiplier
    base_price: { usd: 0, cny: 0 },
    resolution_ratios: {},
    quality_ratios: {},
    duration_unit: 1,
    duration_ratio: 1.0,
    // per_char
    price_per_char: { usd: 0, cny: 0 },
    // per_request
    price_per_request: { usd: 0, cny: 0 },
    // per_second
    price_per_second: { usd: 0, cny: 0 },
    // credit_models
    credit_price: { usd: 0, cny: 0 },
    credit_provider: 'topazlabs',
    active_plan: '',
    plans_json: '',
  });

  useEffect(() => {
    setJsonConfig(config);
    try {
      const parsed = JSON.parse(config);
      setParsedConfig(parsed);
    } catch (e) {
      setParsedConfig(null);
    }
  }, [config]);

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(jsonConfig);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonConfig(formatted);
      onConfigChange(formatted);
      showSuccess('JSON已格式化');
    } catch (e) {
      showError('JSON格式错误：' + e.message);
    }
  };

  const compressJSON = () => {
    try {
      const parsed = JSON.parse(jsonConfig);
      const compressed = JSON.stringify(parsed);
      setJsonConfig(compressed);
      onConfigChange(compressed);
      showSuccess('JSON已压缩');
    } catch (e) {
      showError('JSON格式错误：' + e.message);
    }
  };

  const validateJSON = () => {
    if (verifyJSON(jsonConfig)) {
      showSuccess('JSON格式正确');
    } else {
      showError('JSON格式错误');
    }
  };

  const handleAddModel = () => {
    try {
      if (selectedModelType === 'token_models') validateTimePricing(newModel);
      const parsed = JSON.parse(jsonConfig);
      const modelType = selectedModelType;

      if (!parsed[modelType]) {
        parsed[modelType] = {};
      }

      const configKey = newModel.model.trim();
      if (parsed[modelType][configKey]) {
        showError('模型已存在');
        return;
      }

      if (modelType === 'credit_models') {
        parsed[modelType][configKey] = buildCreditModelConfig();
        const updated = JSON.stringify(parsed, null, 2);
        setJsonConfig(updated);
        onConfigChange(updated);
        setParsedConfig(parsed);

        setShowAddModal(false);
        resetNewModel();
        showSuccess('Credit计费已添加');
        return;
      }

      if (modelType === 'upscale_models') {
        parsed[modelType][configKey] = buildUpscaleModelConfig();
        const updated = JSON.stringify(parsed, null, 2);
        setJsonConfig(updated);
        onConfigChange(updated);
        setParsedConfig(parsed);

        setShowAddModal(false);
        resetNewModel();
        showSuccess('超分模型已添加');
        return;
      }

      if (!newModel.vendor || newModel.vendor.trim() === '') {
        showError('请填写供应商 (vendor)');
        return;
      }

      if (!newModel.category || newModel.category.trim() === '') {
        showError('请填写类别 (category)');
        return;
      }

      // 根据类型构建模型配置
      const modelConfig = {
        model: newModel.model,
        type: newModel.type,
        enabled: newModel.enabled,
        description: newModel.description,
        vendor: newModel.vendor.trim(),
        category: newModel.category.trim(),
      };

      if (selectedModelType === 'token_models') {
        if (newModel.uniform_pricing !== undefined)
          modelConfig.uniform_pricing = newModel.uniform_pricing;
        if (newModel.time_pricing !== undefined)
          modelConfig.time_pricing = newModel.time_pricing;
        const splitPrices = buildCacheCreationPrices(newModel.cache_creation_prices);
        if (splitPrices) modelConfig.cache_creation_prices = splitPrices;
        if ((splitPrices || (newModel.type === 'conditional' && newModel.conditional_rules?.some((rule) => rule.cache_creation_prices))) && newModel.cache_creation_fallback_ttl)
          modelConfig.cache_creation_fallback_ttl = newModel.cache_creation_fallback_ttl;
        if (newModel.type === 'conditional') {
          const cacheRead = cleanPriceObject(newModel.cache_read_price, true);
          if (cacheRead) modelConfig.cache_read_price = cacheRead;
        }
      }

      if (newModel.url && newModel.url.trim() !== '') {
        modelConfig.url = newModel.url.trim();
      }

      // 根据不同类型添加相应的价格配置
      switch (newModel.type) {
        case 'image_tokens':
          if (selectedModelType !== 'token_models') throw new Error('图片 Token 定价必须放在 Token 模型中');
          modelConfig.uniform_pricing = true;
          modelConfig.image_token_pricing = buildImageTokenPricing(newModel);
          break;
        case 'simple':
          const cleanedInputPrice = cleanPriceObject(
            newModel.input_price,
            newModel.uniform_pricing || !!newModel.cache_creation_prices
          );
          const cleanedOutputPrice = cleanPriceObject(
            newModel.output_price,
            newModel.uniform_pricing || !!newModel.cache_creation_prices
          );
          if (cleanedInputPrice) modelConfig.input_price = cleanedInputPrice;
          if (cleanedOutputPrice) modelConfig.output_price = cleanedOutputPrice;
		  if (selectedModelType === 'token_models' && newModel.output_image_price) {
		    modelConfig.output_image_price = cleanPriceObject(newModel.output_image_price);
		  }
          if (selectedModelType === 'token_models') {
            const cleanedCacheCreationPrice = cleanPriceObject(
              newModel.cache_creation_price,
              newModel.uniform_pricing || !!newModel.cache_creation_prices
            );
            const cleanedCacheReadPrice = cleanPriceObject(
              newModel.cache_read_price,
              newModel.uniform_pricing || !!newModel.cache_creation_prices
            );
            if (cleanedCacheCreationPrice)
              modelConfig.cache_creation_price = cleanedCacheCreationPrice;
            if (cleanedCacheReadPrice)
              modelConfig.cache_read_price = cleanedCacheReadPrice;
            const cleanedContextRules = cleanContextRules(
              newModel.context_rules
            );
            if (cleanedContextRules.length > 0)
              modelConfig.context_rules = cleanedContextRules;
          }
          break;
        case 'conditional':
          modelConfig.conditional_rules = cleanConditionalRules(
            newModel.conditional_rules
          );
          break;
        case 'matrix':
          modelConfig.price_matrix = newModel.price_matrix;
          if (newModel.duration_unit)
            modelConfig.duration_unit = newModel.duration_unit;
          if (newModel.duration_ratio)
            modelConfig.duration_ratio = newModel.duration_ratio;
          break;
        case 'multiplier':
          const cleanedBasePrice = cleanPriceObject(newModel.base_price);
          if (cleanedBasePrice) modelConfig.base_price = cleanedBasePrice;
          if (Object.keys(newModel.resolution_ratios).length > 0) {
            modelConfig.resolution_ratios = newModel.resolution_ratios;
          }
          if (Object.keys(newModel.quality_ratios).length > 0) {
            modelConfig.quality_ratios = newModel.quality_ratios;
          }
          if (newModel.duration_unit)
            modelConfig.duration_unit = newModel.duration_unit;
          if (newModel.duration_ratio)
            modelConfig.duration_ratio = newModel.duration_ratio;
          break;
        case 'per_char':
          const cleanedPricePerChar = cleanPriceObject(newModel.price_per_char);
          if (cleanedPricePerChar)
            modelConfig.price_per_char = cleanedPricePerChar;
          if (Object.keys(newModel.quality_ratios).length > 0) {
            modelConfig.quality_ratios = newModel.quality_ratios;
          }
          break;
        case 'per_request':
          const cleanedPricePerRequest = cleanPriceObject(
            newModel.price_per_request
          );
          if (cleanedPricePerRequest)
            modelConfig.price_per_request = cleanedPricePerRequest;
          break;
        case 'per_second':
          const cleanedPricePerSecond = cleanPriceObject(
            newModel.price_per_second
          );
          if (cleanedPricePerSecond)
            modelConfig.price_per_second = cleanedPricePerSecond;
          break;
        default:
          break;
      }

      parsed[modelType][newModel.model] = modelConfig;

      const updated = JSON.stringify(parsed, null, 2);
      setJsonConfig(updated);
      onConfigChange(updated);
      setParsedConfig(parsed);

      setShowAddModal(false);
      resetNewModel();
      showSuccess('模型已添加');
    } catch (e) {
      showError('添加失败：' + e.message);
    }
  };

  const handleDeleteModel = (modelType, modelName) => {
    try {
      const parsed = JSON.parse(jsonConfig);
      delete parsed[modelType][modelName];

      const updated = JSON.stringify(parsed, null, 2);
      setJsonConfig(updated);
      onConfigChange(updated);
      setParsedConfig(parsed);
      showSuccess('模型已删除');
    } catch (e) {
      showError('删除失败：' + e.message);
    }
  };

  const handleEditModel = (modelType, modelName, modelData) => {
    setEditingModel({ modelType, modelName, data: modelData });
    setNewModel({
      model: modelName,
      type: modelData.type || 'simple',
      enabled: modelData.enabled !== false,
      description: modelData.description || '',
      category: modelData.category || '',
      vendor: modelData.vendor || '',
      url: modelData.url || '',
      image_token_pricing: modelData.image_token_pricing,
      input_price: modelData.input_price || { usd: 0, cny: 0 },
      output_price: modelData.output_price || { usd: 0, cny: 0 },
	  output_image_price: modelData.output_image_price,
      cache_creation_price:
        modelData.cache_creation_price ||
        (modelData.uniform_pricing ? {} : { usd: 0, cny: 0 }),
      cache_read_price:
        modelData.cache_read_price ||
        (modelData.uniform_pricing ? {} : { usd: 0, cny: 0 }),
      conditional_rules: modelData.conditional_rules || [],
      context_rules: modelData.context_rules || [],
      uniform_pricing: modelData.uniform_pricing,
      time_pricing: modelData.time_pricing,
      cache_creation_prices: modelData.cache_creation_prices,
      cache_creation_fallback_ttl: modelData.cache_creation_fallback_ttl,
      price_matrix: modelData.price_matrix || {},
      base_price: modelData.base_price || { usd: 0, cny: 0 },
      resolution_ratios: modelData.resolution_ratios || {},
      quality_ratios: modelData.quality_ratios || {},
      duration_unit: modelData.duration_unit || 1,
      duration_ratio: modelData.duration_ratio || 1.0,
      price_per_char: modelData.price_per_char || { usd: 0, cny: 0 },
      price_per_request: modelData.price_per_request || { usd: 0, cny: 0 },
      price_per_second: modelData.price_per_second || { usd: 0, cny: 0 },
      credit_price: modelData.credit_price || { usd: 0, cny: 0 },
      credit_provider: modelData.credit_provider || 'topazlabs',
      active_plan: modelData.active_plan || '',
      plans_json: modelData.plans
        ? JSON.stringify(modelData.plans, null, 2)
        : '',
    });
    setSelectedModelType(modelType);
    setShowAddModal(true);
  };

  const handleCopyModel = (modelType, modelName, modelData) => {
    setEditingModel(null); // 复制时不设置editingModel
    setNewModel({
      model: modelName + '-copy', // 添加-copy后缀
      type: modelData.type || 'simple',
      enabled: modelData.enabled !== false,
      description: modelData.description || '',
      category: modelData.category || '',
      vendor: modelData.vendor || '',
      url: modelData.url || '',
      image_token_pricing: modelData.image_token_pricing,
      input_price: modelData.input_price || { usd: 0, cny: 0 },
      output_price: modelData.output_price || { usd: 0, cny: 0 },
	  output_image_price: modelData.output_image_price,
      cache_creation_price:
        modelData.cache_creation_price ||
        (modelData.uniform_pricing ? {} : { usd: 0, cny: 0 }),
      cache_read_price:
        modelData.cache_read_price ||
        (modelData.uniform_pricing ? {} : { usd: 0, cny: 0 }),
      conditional_rules: modelData.conditional_rules || [],
      context_rules: modelData.context_rules || [],
      uniform_pricing: modelData.uniform_pricing,
      time_pricing: modelData.time_pricing,
      cache_creation_prices: modelData.cache_creation_prices,
      cache_creation_fallback_ttl: modelData.cache_creation_fallback_ttl,
      price_matrix: modelData.price_matrix || {},
      base_price: modelData.base_price || { usd: 0, cny: 0 },
      resolution_ratios: modelData.resolution_ratios || {},
      quality_ratios: modelData.quality_ratios || {},
      duration_unit: modelData.duration_unit || 1,
      duration_ratio: modelData.duration_ratio || 1.0,
      price_per_char: modelData.price_per_char || { usd: 0, cny: 0 },
      price_per_request: modelData.price_per_request || { usd: 0, cny: 0 },
      price_per_second: modelData.price_per_second || { usd: 0, cny: 0 },
      credit_price: modelData.credit_price || { usd: 0, cny: 0 },
      credit_provider: modelData.credit_provider || 'topazlabs',
      active_plan: modelData.active_plan || '',
      plans_json: modelData.plans
        ? JSON.stringify(modelData.plans, null, 2)
        : '',
    });
    setSelectedModelType(modelType);
    setShowAddModal(true);
  };

  const handleUpdateModel = () => {
    try {
      if (selectedModelType === 'token_models') validateTimePricing(newModel);
      const parsed = JSON.parse(jsonConfig);
      const { modelType, modelName } = editingModel;

      if (modelType === 'credit_models') {
        delete parsed[modelType][modelName];
        parsed[modelType][newModel.model.trim()] = buildCreditModelConfig();

        const updated = JSON.stringify(parsed, null, 2);
        setJsonConfig(updated);
        onConfigChange(updated);
        setParsedConfig(parsed);

        setShowAddModal(false);
        setEditingModel(null);
        resetNewModel();
        showSuccess('Credit计费已更新');
        return;
      }

      if (modelType === 'upscale_models') {
        delete parsed[modelType][modelName];
        parsed[modelType][newModel.model.trim()] = buildUpscaleModelConfig();

        const updated = JSON.stringify(parsed, null, 2);
        setJsonConfig(updated);
        onConfigChange(updated);
        setParsedConfig(parsed);

        setShowAddModal(false);
        setEditingModel(null);
        resetNewModel();
        showSuccess('超分模型已更新');
        return;
      }

      if (!newModel.vendor || newModel.vendor.trim() === '') {
        showError('请填写供应商 (vendor)');
        return;
      }

      if (!newModel.category || newModel.category.trim() === '') {
        showError('请填写类别 (category)');
        return;
      }

      // 删除旧的
      delete parsed[modelType][modelName];

      // 添加新的
      const modelConfig = {
        model: newModel.model,
        type: newModel.type,
        enabled: newModel.enabled,
        description: newModel.description,
        vendor: newModel.vendor.trim(),
        category: newModel.category.trim(),
      };

      if (selectedModelType === 'token_models') {
        if (newModel.uniform_pricing !== undefined)
          modelConfig.uniform_pricing = newModel.uniform_pricing;
        if (newModel.time_pricing !== undefined)
          modelConfig.time_pricing = newModel.time_pricing;
        const splitPrices = buildCacheCreationPrices(newModel.cache_creation_prices);
        if (splitPrices) modelConfig.cache_creation_prices = splitPrices;
        if ((splitPrices || (newModel.type === 'conditional' && newModel.conditional_rules?.some((rule) => rule.cache_creation_prices))) && newModel.cache_creation_fallback_ttl)
          modelConfig.cache_creation_fallback_ttl = newModel.cache_creation_fallback_ttl;
        if (newModel.type === 'conditional') {
          const cacheRead = cleanPriceObject(newModel.cache_read_price, true);
          if (cacheRead) modelConfig.cache_read_price = cacheRead;
        }
      }

      if (newModel.url && newModel.url.trim() !== '') {
        modelConfig.url = newModel.url.trim();
      }

      // 根据不同类型添加相应的价格配置
      switch (newModel.type) {
        case 'image_tokens':
          if (selectedModelType !== 'token_models') throw new Error('图片 Token 定价必须放在 Token 模型中');
          modelConfig.uniform_pricing = true;
          modelConfig.image_token_pricing = buildImageTokenPricing(newModel);
          break;
        case 'simple':
          const cleanedInputPrice2 = cleanPriceObject(
            newModel.input_price,
            newModel.uniform_pricing || !!newModel.cache_creation_prices
          );
          const cleanedOutputPrice2 = cleanPriceObject(
            newModel.output_price,
            newModel.uniform_pricing || !!newModel.cache_creation_prices
          );
          if (cleanedInputPrice2) modelConfig.input_price = cleanedInputPrice2;
          if (cleanedOutputPrice2)
            modelConfig.output_price = cleanedOutputPrice2;
		  if (selectedModelType === 'token_models' && newModel.output_image_price) {
		    modelConfig.output_image_price = cleanPriceObject(newModel.output_image_price);
		  }
          if (selectedModelType === 'token_models') {
            const cleanedCacheCreationPrice2 = cleanPriceObject(
              newModel.cache_creation_price,
              newModel.uniform_pricing || !!newModel.cache_creation_prices
            );
            const cleanedCacheReadPrice2 = cleanPriceObject(
              newModel.cache_read_price,
              newModel.uniform_pricing || !!newModel.cache_creation_prices
            );
            if (cleanedCacheCreationPrice2)
              modelConfig.cache_creation_price = cleanedCacheCreationPrice2;
            if (cleanedCacheReadPrice2)
              modelConfig.cache_read_price = cleanedCacheReadPrice2;
            const cleanedContextRules2 = cleanContextRules(
              newModel.context_rules
            );
            if (cleanedContextRules2.length > 0)
              modelConfig.context_rules = cleanedContextRules2;
          }
          break;
        case 'conditional':
          modelConfig.conditional_rules = cleanConditionalRules(
            newModel.conditional_rules
          );
          break;
        case 'matrix':
          modelConfig.price_matrix = newModel.price_matrix;
          if (newModel.duration_unit)
            modelConfig.duration_unit = newModel.duration_unit;
          if (newModel.duration_ratio)
            modelConfig.duration_ratio = newModel.duration_ratio;
          break;
        case 'multiplier':
          const cleanedBasePrice2 = cleanPriceObject(newModel.base_price);
          if (cleanedBasePrice2) modelConfig.base_price = cleanedBasePrice2;
          if (Object.keys(newModel.resolution_ratios).length > 0) {
            modelConfig.resolution_ratios = newModel.resolution_ratios;
          }
          if (Object.keys(newModel.quality_ratios).length > 0) {
            modelConfig.quality_ratios = newModel.quality_ratios;
          }
          if (newModel.duration_unit)
            modelConfig.duration_unit = newModel.duration_unit;
          if (newModel.duration_ratio)
            modelConfig.duration_ratio = newModel.duration_ratio;
          break;
        case 'per_char':
          const cleanedPricePerChar2 = cleanPriceObject(
            newModel.price_per_char
          );
          if (cleanedPricePerChar2)
            modelConfig.price_per_char = cleanedPricePerChar2;
          if (Object.keys(newModel.quality_ratios).length > 0) {
            modelConfig.quality_ratios = newModel.quality_ratios;
          }
          break;
        case 'per_request':
          const cleanedPricePerRequest2 = cleanPriceObject(
            newModel.price_per_request
          );
          if (cleanedPricePerRequest2)
            modelConfig.price_per_request = cleanedPricePerRequest2;
          break;
        case 'per_second':
          const cleanedPricePerSecond2 = cleanPriceObject(
            newModel.price_per_second
          );
          if (cleanedPricePerSecond2)
            modelConfig.price_per_second = cleanedPricePerSecond2;
          break;
        default:
          break;
      }

      parsed[modelType][newModel.model] = modelConfig;

      const updated = JSON.stringify(parsed, null, 2);
      setJsonConfig(updated);
      onConfigChange(updated);
      setParsedConfig(parsed);

      setShowAddModal(false);
      setEditingModel(null);
      resetNewModel();
      showSuccess('模型已更新');
    } catch (e) {
      showError('更新失败：' + e.message);
    }
  };

  const resetNewModel = () => {
    setNewModel({
      model: '',
      type: 'simple',
      enabled: true,
      description: '',
      category: '',
      vendor: '',
      url: '',
      input_price: { usd: 0, cny: 0 },
      output_price: { usd: 0, cny: 0 },
      cache_creation_price: { usd: 0, cny: 0 },
      cache_read_price: { usd: 0, cny: 0 },
      conditional_rules: [],
      context_rules: [],
      price_matrix: {},
      base_price: { usd: 0, cny: 0 },
      resolution_ratios: {},
      quality_ratios: {},
      duration_unit: 1,
      duration_ratio: 1.0,
      price_per_char: { usd: 0, cny: 0 },
      price_per_request: { usd: 0, cny: 0 },
      price_per_second: { usd: 0, cny: 0 },
      credit_price: { usd: 0, cny: 0 },
      credit_provider: 'topazlabs',
      active_plan: '',
      plans_json: '',
    });
    setEditingModel(null);
  };

  const renderPriceInfo = (data, modelType) => {
    const style = { fontSize: '11px', lineHeight: '1.4' };

    if (modelType === 'credit_models') {
      const planCount = data.plans ? Object.keys(data.plans).length : 0;
      return (
        <div style={style}>
          <div>
            <strong>每 Credit:</strong>{' '}
            {data.credit_price?.usd && `$${data.credit_price.usd}`}
            {data.credit_price?.usd && data.credit_price?.cny && ' / '}
            {data.credit_price?.cny && `¥${data.credit_price.cny}`}
          </div>
          {data.active_plan && (
            <div>
              <strong>当前套餐:</strong> {data.active_plan}
            </div>
          )}
          {planCount > 0 && (
            <div>
              <strong>套餐:</strong> {planCount} 个
            </div>
          )}
        </div>
      );
    }

    if (modelType === 'upscale_models') {
      return (
        <div style={style}>
          <div>
            <strong>Credit Provider:</strong>{' '}
            {data.credit_provider || 'topazlabs'}
          </div>
        </div>
      );
    }

    switch (data.type) {
      case 'image_tokens': return <ImageTokenPricingDetails pricing={data} />;
      case 'simple':
        return (
          <div style={style}>
            <TimePricingDetails pricing={data} />
            <div>
              <strong>输入:</strong>{' '}
              {data.input_price?.usd && `$${data.input_price.usd}`}
              {data.input_price?.usd && data.input_price?.cny && ' / '}
              {data.input_price?.cny && `¥${data.input_price.cny}`}
            </div>
            <div>
              <strong>{data.output_image_price ? '文字/思考输出:' : '输出:'}</strong>{' '}
              {data.output_price?.usd && `$${data.output_price.usd}`}
              {data.output_price?.usd && data.output_price?.cny && ' / '}
              {data.output_price?.cny && `¥${data.output_price.cny}`}
            </div>
		    {data.output_image_price && (
		      <div><strong>图片输出:</strong> ${data.output_image_price.usd}/1M tokens</div>
		    )}
            {(data.cache_creation_price || data.cache_read_price) && (
              <>
                {data.cache_creation_price && (
                  <div>
                    <strong>缓存写入:</strong>{' '}
                    {data.cache_creation_price?.usd &&
                      `$${data.cache_creation_price.usd}`}
                    {data.cache_creation_price?.usd &&
                      data.cache_creation_price?.cny &&
                      ' / '}
                    {data.cache_creation_price?.cny &&
                      `¥${data.cache_creation_price.cny}`}
                  </div>
                )}
                {data.cache_read_price && (
                  <div>
                    <strong>缓存读取:</strong>{' '}
                    {data.cache_read_price?.usd &&
                      `$${data.cache_read_price.usd}`}
                    {data.cache_read_price?.usd &&
                      data.cache_read_price?.cny &&
                      ' / '}
                    {data.cache_read_price?.cny &&
                      `¥${data.cache_read_price.cny}`}
                  </div>
                )}
              </>
            )}
            {data.context_rules && data.context_rules.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                <strong>上下文规则:</strong> {data.context_rules.length} 个
              </div>
            )}
          </div>
        );

      case 'conditional':
        if (!data.conditional_rules || data.conditional_rules.length === 0) {
          return <em style={style}>无规则</em>;
        }
        return (
          <div style={style}>
            <div>
              <strong>条件定价:</strong> {data.conditional_rules.length} 个规则
            </div>
            {data.conditional_rules.slice(0, 2).map((rule, idx) => (
              <div key={idx} style={{ marginTop: '2px' }}>
                • {rule.input_min}-
                {rule.input_max === -1 ? '∞' : rule.input_max}
                tokens: {rule.input_price?.usd && `$${rule.input_price.usd}`}
                {rule.input_price?.cny && `¥${rule.input_price.cny}`}
                {(rule.cache_creation_price || rule.cache_read_price) &&
                  ' / cache'}
              </div>
            ))}
            {data.conditional_rules.length > 2 && (
              <div style={{ marginTop: '2px', color: '#888' }}>
                ...还有 {data.conditional_rules.length - 2} 个规则
              </div>
            )}
          </div>
        );

      case 'matrix':
        if (!data.price_matrix) {
          return <em style={style}>无矩阵</em>;
        }
        const matrixKeys = Object.keys(data.price_matrix);
        return (
          <div style={style}>
            <div>
              <strong>矩阵定价:</strong> {matrixKeys.length} 个维度
            </div>
            {matrixKeys.slice(0, 2).map((key) => {
              const value = data.price_matrix[key];
              if (typeof value === 'object' && !value.usd && !value.cny) {
                const subKey = Object.keys(value)[0];
                const price = value[subKey];
                return (
                  <div key={key} style={{ marginTop: '2px' }}>
                    • {key}/{subKey}: {price?.usd && `$${price.usd}`}
                    {price?.cny && `¥${price.cny}`}
                  </div>
                );
              } else {
                return (
                  <div key={key} style={{ marginTop: '2px' }}>
                    • {key}: {value?.usd && `$${value.usd}`}
                    {value?.cny && `¥${value.cny}`}
                  </div>
                );
              }
            })}
            {matrixKeys.length > 2 && (
              <div style={{ marginTop: '2px', color: '#888' }}>
                ...还有 {matrixKeys.length - 2} 个维度
              </div>
            )}
          </div>
        );

      case 'multiplier':
        return (
          <div style={style}>
            <div>
              <strong>基础价格:</strong>{' '}
              {data.base_price?.usd && `$${data.base_price.usd}`}
              {data.base_price?.usd && data.base_price?.cny && ' / '}
              {data.base_price?.cny && `¥${data.base_price.cny}`}
            </div>
            {data.resolution_ratios && (
              <div style={{ marginTop: '2px' }}>
                <strong>分辨率倍率:</strong>{' '}
                {Object.keys(data.resolution_ratios).length} 个
              </div>
            )}
            {data.quality_ratios && (
              <div style={{ marginTop: '2px' }}>
                <strong>质量倍率:</strong>{' '}
                {Object.keys(data.quality_ratios).length} 个
              </div>
            )}
          </div>
        );

      case 'per_char':
        return (
          <div style={style}>
            <div>
              <strong>每字符:</strong>{' '}
              {data.price_per_char?.usd && `$${data.price_per_char.usd}`}
              {data.price_per_char?.usd && data.price_per_char?.cny && ' / '}
              {data.price_per_char?.cny && `¥${data.price_per_char.cny}`}
            </div>
            {data.quality_ratios && (
              <div style={{ marginTop: '2px' }}>
                <strong>质量倍率:</strong>{' '}
                {Object.keys(data.quality_ratios).length} 个
              </div>
            )}
          </div>
        );

      case 'per_request':
        return (
          <div style={style}>
            <div>
              <strong>每次请求:</strong>{' '}
              {data.price_per_request?.usd && `$${data.price_per_request.usd}`}
              {data.price_per_request?.usd &&
                data.price_per_request?.cny &&
                ' / '}
              {data.price_per_request?.cny && `¥${data.price_per_request.cny}`}
            </div>
          </div>
        );

      case 'per_second':
        return (
          <div style={style}>
            <div>
              <strong>每秒:</strong>{' '}
              {data.price_per_second?.usd && `$${data.price_per_second.usd}`}
              {data.price_per_second?.usd &&
                data.price_per_second?.cny &&
                ' / '}
              {data.price_per_second?.cny && `¥${data.price_per_second.cny}`}
            </div>
          </div>
        );

      default:
        return <em style={style}>未知类型</em>;
    }
  };

  const renderModelTable = (modelType, models) => {
    if (!models) return null;

    const modelEntries = Object.entries(models).filter(([name]) =>
      name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (modelEntries.length === 0) return null;

    return (
      <Segment>
        <Header as='h4'>
          {modelType === 'token_models' && 'Token模型'}
          {modelType === 'image_models' && '图像模型'}
          {modelType === 'video_models' && '视频模型'}
          {modelType === 'audio_models' && '音频模型'}
          {modelType === 'upscale_models' && '超分模型'}
          {modelType === 'credit_models' && '官方Credit计费'}
          <Label circular>{modelEntries.length}</Label>
        </Header>
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={3}>模型名称</Table.HeaderCell>
              <Table.HeaderCell width={2}>类型</Table.HeaderCell>
              <Table.HeaderCell width={1}>状态</Table.HeaderCell>
              <Table.HeaderCell width={4}>价格</Table.HeaderCell>
              <Table.HeaderCell width={4}>描述</Table.HeaderCell>
              <Table.HeaderCell width={2}>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {modelEntries.map(([name, data]) => (
              <Table.Row key={name}>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{name}</code>
                </Table.Cell>
                <Table.Cell>
                  <Label size='small' color='blue'>
                    {modelType === 'credit_models' ? 'credit' : data.type}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {data.enabled !== false ? (
                    <Label size='small' color='green'>
                      启用
                    </Label>
                  ) : (
                    <Label size='small' color='grey'>
                      禁用
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell style={{ fontSize: '11px' }}>
                  {renderPriceInfo(data, modelType)}
                </Table.Cell>
                <Table.Cell style={{ fontSize: '11px' }}>
                  {data.description || '-'}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size='mini'
                    icon='copy'
                    title='复制模型'
                    onClick={() => handleCopyModel(modelType, name, data)}
                  />
                  <Button
                    size='mini'
                    icon='edit'
                    title='编辑模型'
                    onClick={() => handleEditModel(modelType, name, data)}
                  />
                  <Button
                    size='mini'
                    icon='trash'
                    color='red'
                    title='删除模型'
                    onClick={() => {
                      if (window.confirm(`确定删除模型 ${name}？`)) {
                        handleDeleteModel(modelType, name);
                      }
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  const modelTypeOptions = [
    { key: 'token', text: 'Token模型', value: 'token_models' },
    { key: 'image', text: '图像模型', value: 'image_models' },
    { key: 'video', text: '视频模型', value: 'video_models' },
    { key: 'audio', text: '音频模型', value: 'audio_models' },
    { key: 'upscale', text: '超分模型', value: 'upscale_models' },
    { key: 'credit', text: '官方Credit计费', value: 'credit_models' },
  ];

  const pricingTypeOptions = [
    { key: 'image_tokens', text: '图片 Token 明细定价', value: 'image_tokens' },
    { key: 'simple', text: '简单定价', value: 'simple' },
    { key: 'conditional', text: '条件定价', value: 'conditional' },
    { key: 'matrix', text: '矩阵定价', value: 'matrix' },
    { key: 'multiplier', text: '倍率定价', value: 'multiplier' },
    { key: 'per_char', text: '按字符计费', value: 'per_char' },
    { key: 'per_request', text: '按请求计费', value: 'per_request' },
    { key: 'per_second', text: '按时长计费', value: 'per_second' },
    { key: 'credit', text: '官方Credit计费', value: 'credit' },
  ];

  const panes = [
    {
      menuItem: { key: 'visual', icon: 'list', content: '可视化编辑' },
      render: () => (
        <Tab.Pane>
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <Form.Input
                  icon='search'
                  placeholder='搜索模型...'
                  value={searchQuery}
                  onChange={(e, { value }) => setSearchQuery(value)}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Button
                  primary
                  fluid
                  icon='plus'
                  content='添加模型'
                  onClick={() => {
                    resetNewModel();
                    setShowAddModal(true);
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>

          {parsedConfig && (
            <>
              {renderModelTable('token_models', parsedConfig.token_models)}
              {renderModelTable('image_models', parsedConfig.image_models)}
              {renderModelTable('video_models', parsedConfig.video_models)}
              {renderModelTable('audio_models', parsedConfig.audio_models)}
              {renderModelTable('upscale_models', parsedConfig.upscale_models)}
              {renderModelTable('credit_models', parsedConfig.credit_models)}
            </>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: 'json', icon: 'code', content: 'JSON编辑' },
      render: () => (
        <Tab.Pane>
          <Button.Group size='small' style={{ marginBottom: '10px' }}>
            <Button icon='magic' content='格式化' onClick={formatJSON} />
            <Button icon='compress' content='压缩' onClick={compressJSON} />
            <Button icon='check' content='验证' onClick={validateJSON} />
          </Button.Group>
          <Form.TextArea
            value={jsonConfig}
            onChange={(e, { value }) => {
              setJsonConfig(value);
              onConfigChange(value);
            }}
            style={{
              minHeight: 500,
              fontFamily: 'JetBrains Mono, Consolas, monospace',
              fontSize: '12px',
            }}
          />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      {stats && (
        <Message info>
          <Message.Header>当前配置统计</Message.Header>
          <p>
            Token模型: {stats.token_models_count} 个 | 图像模型:{' '}
            {stats.image_models_count} 个 | 视频模型: {stats.video_models_count}{' '}
            个 | 音频模型: {stats.audio_models_count} 个 | 超分模型:{' '}
            {stats.upscale_models_count || 0} 个 | Credit计费:{' '}
            {stats.credit_models_count || 0} 个
          </p>
          <p>
            配置版本: {stats.version} | 更新时间: {stats.updated_at}
          </p>
        </Message>
      )}

      <Tab panes={panes} />

      {/* 添加/编辑模型模态框 */}
      <Modal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetNewModel();
        }}
        size='small'
      >
        <Modal.Header>{editingModel ? '编辑模型' : '添加新模型'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>模型类型</label>
                <Dropdown
                  selection
                  options={modelTypeOptions}
                  value={selectedModelType}
                  onChange={(e, { value }) => {
                    setSelectedModelType(value);
                    if (value === 'upscale_models') {
                      setNewModel({
                        ...newModel,
                        type: 'credit',
                        credit_provider:
                          newModel.credit_provider || 'topazlabs',
                      });
                    }
                  }}
                  disabled={!!editingModel}
                />
              </Form.Field>
              <Form.Field>
                <label>定价类型</label>
                <Dropdown
                  selection
                  options={pricingTypeOptions}
                  value={newModel.type}
                  onChange={(e, { value }) =>
                    setNewModel({ ...newModel, type: value })
                  }
                  disabled={isCreditModelType || isUpscaleModelType}
                />
              </Form.Field>
            </Form.Group>

            <Form.Input
              label={isCreditModelType ? 'Provider' : '模型名称'}
              placeholder={
                isCreditModelType ? '例如: topazlabs' : '例如: gpt-4o'
              }
              value={newModel.model}
              onChange={(e, { value }) =>
                setNewModel({ ...newModel, model: value })
              }
              disabled={!!editingModel}
            />

            {!isCreditModelType && (
              <Form.Group widths='equal'>
                <Form.Input
                  label='供应商 (vendor) *'
                  placeholder='例如: OpenAI'
                  value={newModel.vendor}
                  onChange={(e, { value }) =>
                    setNewModel({ ...newModel, vendor: value })
                  }
                  required
                />
                <Form.Input
                  label='类别 (category) *'
                  placeholder='例如: 对话模型'
                  value={newModel.category}
                  onChange={(e, { value }) =>
                    setNewModel({ ...newModel, category: value })
                  }
                  required
                />
              </Form.Group>
            )}

            {isCreditModelType && (
              <Form.Input
                label='供应商 (vendor)'
                placeholder='例如: TopazLabs'
                value={newModel.vendor}
                onChange={(e, { value }) =>
                  setNewModel({ ...newModel, vendor: value })
                }
              />
            )}

            {isUpscaleModelType && (
              <Form.Input
                label='Credit Provider'
                placeholder='例如: topazlabs'
                value={newModel.credit_provider}
                onChange={(e, { value }) =>
                  setNewModel({ ...newModel, credit_provider: value })
                }
              />
            )}

            <Form.Input
              label='文档链接 (url)'
              placeholder='https://...'
              value={newModel.url}
              onChange={(e, { value }) =>
                setNewModel({ ...newModel, url: value })
              }
            />

            <Form.Input
              label='描述'
              placeholder='模型描述'
              value={newModel.description}
              onChange={(e, { value }) =>
                setNewModel({ ...newModel, description: value })
              }
            />

            <Form.Checkbox
              label='启用此模型'
              checked={newModel.enabled}
              onChange={(e, { checked }) =>
                setNewModel({ ...newModel, enabled: checked })
              }
            />

            {isCreditModelType && (
              <>
                <Divider horizontal>每 Credit 单价</Divider>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='USD'
                    type='number'
                    step='0.001'
                    value={newModel.credit_price.usd}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        credit_price: {
                          ...newModel.credit_price,
                          usd: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                  <Form.Input
                    label='CNY'
                    type='number'
                    step='0.001'
                    value={newModel.credit_price.cny}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        credit_price: {
                          ...newModel.credit_price,
                          cny: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                </Form.Group>
                <Form.Input
                  label='当前套餐 (active_plan)'
                  placeholder='例如: developer'
                  value={newModel.active_plan}
                  onChange={(e, { value }) =>
                    setNewModel({ ...newModel, active_plan: value })
                  }
                />
                <Form.TextArea
                  label='套餐价格 (plans JSON)'
                  placeholder='{"starter":{"usd":0.12},"developer":{"usd":0.1},"scale":{"usd":0.08}}'
                  value={newModel.plans_json}
                  onChange={(e, { value }) =>
                    setNewModel({ ...newModel, plans_json: value })
                  }
                  style={{
                    minHeight: 120,
                    fontFamily: 'JetBrains Mono, Consolas, monospace',
                    fontSize: '12px',
                  }}
                />
              </>
            )}

            {selectedModelType === 'token_models' && newModel.type === 'image_tokens' && (<ImageTokenPricingEditor model={newModel} onChange={setNewModel} />)}

            {selectedModelType === 'token_models' &&
              newModel.type === 'simple' && (
                <TimePricingEditor model={newModel} onChange={setNewModel} />
              )}


            {selectedModelType === 'token_models' &&
              ['simple', 'conditional'].includes(newModel.type) && (
                <>
                  <Divider horizontal>按缓存时长计费（每百万 Token）</Divider>
                  <CacheCreationPricingEditor
                    prices={newModel.cache_creation_prices}
                    onChange={(value) => setNewModel({ ...newModel, cache_creation_prices: value })}
                  />
                  {(newModel.cache_creation_prices || newModel.conditional_rules?.some((rule) => rule.cache_creation_prices)) && (
                    <Form.Select
                      label='上游缺少缓存写入时长明细时'
                      value={newModel.cache_creation_fallback_ttl || ''}
                      options={[
                        { key: 'strict', value: '', text: '要求明细，缺少时结算失败（推荐）' },
                        { key: '5m', value: '5m', text: '已确认上游固定为 5 分钟' },
                        { key: '1h', value: '1h', text: '已确认上游固定为 1 小时' },
                      ]}
                      onChange={(e, { value }) => setNewModel({ ...newModel, cache_creation_fallback_ttl: value })}
                    />
                  )}
                </>
              )}

            {!isCreditModelType &&
              !isUpscaleModelType &&
              !(
                selectedModelType === 'token_models' && newModel.time_pricing
              ) &&
              newModel.type === 'simple' && (
                <>
                  <Divider horizontal>输入价格（每百万Token）</Divider>
                  <Form.Group widths='equal'>
                    <Form.Input
                      label='USD'
                      type='number'
                      step='0.01'
                      value={newModel.input_price.usd}
                      onChange={(e, { value }) =>
                        setNewModel({
                          ...newModel,
                          input_price: {
                            ...newModel.input_price,
                            usd: parseFloat(value) || 0,
                          },
                        })
                      }
                    />
                    <Form.Input
                      label='CNY'
                      type='number'
                      step='0.01'
                      value={newModel.input_price.cny}
                      onChange={(e, { value }) =>
                        setNewModel({
                          ...newModel,
                          input_price: {
                            ...newModel.input_price,
                            cny: parseFloat(value) || 0,
                          },
                        })
                      }
                    />
                  </Form.Group>

                  <Divider horizontal>{newModel.output_image_price ? '文字与思考输出价格（每百万Token）' : '输出价格（每百万Token）'}</Divider>
                  <Form.Group widths='equal'>
                    <Form.Input
                      label='USD'
                      type='number'
                      step='0.01'
                      value={newModel.output_price.usd}
                      onChange={(e, { value }) =>
                        setNewModel({
                          ...newModel,
                          output_price: {
                            ...newModel.output_price,
                            usd: parseFloat(value) || 0,
                          },
                        })
                      }
                    />
                    <Form.Input
                      label='CNY'
                      type='number'
                      step='0.01'
                      value={newModel.output_price.cny}
                      onChange={(e, { value }) =>
                        setNewModel({
                          ...newModel,
                          output_price: {
                            ...newModel.output_price,
                            cny: parseFloat(value) || 0,
                          },
                        })
                      }
                    />
                  </Form.Group>

                  {selectedModelType === 'token_models' && (
                    <>
					  {(newModel.output_image_price || newModel.model === 'gemini-nano-banana-2.1') && (
					    <>
					      <Divider horizontal>图片输出价格（每百万Token）</Divider>
                            <Form.Input id='gemini-image-output-price' label='图片输出 USD' type='number' step='0.01'
					        value={newModel.output_image_price?.usd ?? ''}
					        onChange={(e, { value }) => setNewModel({ ...newModel, output_image_price: { usd: parseFloat(value) || 0 } })} />
					    </>
					  )}
                      <Divider horizontal>缓存写入价格（每百万Token）</Divider>
                      <Message info size='small'>
                        未填写则使用 Input Price 作为 fallback
                      </Message>
                      <Form.Group widths='equal'>
                        <Form.Input
                          label='USD'
                          type='number'
                          step='0.01'
                          placeholder='未填写则使用 Input Price'
                          value={newModel.cache_creation_price?.usd || ''}
                          onChange={(e, { value }) =>
                            setNewModel({
                              ...newModel,
                              cache_creation_price: {
                                ...newModel.cache_creation_price,
                                usd: parseFloat(value) || 0,
                              },
                            })
                          }
                        />
                        <Form.Input
                          label='CNY'
                          type='number'
                          step='0.01'
                          placeholder='未填写则使用 Input Price'
                          value={newModel.cache_creation_price?.cny || ''}
                          onChange={(e, { value }) =>
                            setNewModel({
                              ...newModel,
                              cache_creation_price: {
                                ...newModel.cache_creation_price,
                                cny: parseFloat(value) || 0,
                              },
                            })
                          }
                        />
                      </Form.Group>

                      <Divider horizontal>缓存读取价格（每百万Token）</Divider>
                      <Form.Group widths='equal'>
                        <Form.Input
                          label='USD'
                          type='number'
                          step='0.01'
                          placeholder='未填写则使用 Input Price'
                          value={newModel.cache_read_price?.usd || ''}
                          onChange={(e, { value }) =>
                            setNewModel({
                              ...newModel,
                              cache_read_price: {
                                ...newModel.cache_read_price,
                                usd: parseFloat(value) || 0,
                              },
                            })
                          }
                        />
                        <Form.Input
                          label='CNY'
                          type='number'
                          step='0.01'
                          placeholder='未填写则使用 Input Price'
                          value={newModel.cache_read_price?.cny || ''}
                          onChange={(e, { value }) =>
                            setNewModel({
                              ...newModel,
                              cache_read_price: {
                                ...newModel.cache_read_price,
                                cny: parseFloat(value) || 0,
                              },
                            })
                          }
                        />
                      </Form.Group>

                      <Divider horizontal>上下文计费规则</Divider>
                      <Message info size='small'>
                        常见用法：例如可按 `has_audio`、`has_reference_video` 或
                        `resolution=1080p`
                        覆盖输出价格；多条规则按顺序匹配，越具体的规则应放前面。
                      </Message>
                      {newModel.context_rules.length > 0 ? (
                        <>
                          {newModel.context_rules.map((rule, index) => (
                            <Segment
                              key={index}
                              style={{ marginBottom: '10px' }}
                            >
                              <Grid>
                                <Grid.Row>
                                  <Grid.Column width={16}>
                                    <Label color='teal' ribbon>
                                      规则 {index + 1}
                                    </Label>
                                    <Button
                                      floated='right'
                                      size='mini'
                                      color='red'
                                      icon='trash'
                                      onClick={() => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules.splice(index, 1);
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row columns={2}>
                                  <Grid.Column>
                                    <label>参考视频</label>
                                    <Dropdown
                                      fluid
                                      selection
                                      options={[
                                        {
                                          key: 'unset-ref',
                                          text: '不限制',
                                          value: 'unset',
                                        },
                                        {
                                          key: 'true-ref',
                                          text: '有参考视频',
                                          value: 'true',
                                        },
                                        {
                                          key: 'false-ref',
                                          text: '无参考视频',
                                          value: 'false',
                                        },
                                      ]}
                                      value={
                                        rule?.when?.has_reference_video ===
                                        undefined
                                          ? 'unset'
                                          : String(
                                              rule.when.has_reference_video
                                            )
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          when: {
                                            ...newRules[index].when,
                                            has_reference_video:
                                              value === 'unset'
                                                ? undefined
                                                : value === 'true',
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                  <Grid.Column>
                                    <label>音频</label>
                                    <Dropdown
                                      fluid
                                      selection
                                      options={[
                                        {
                                          key: 'unset-audio',
                                          text: '不限制',
                                          value: 'unset',
                                        },
                                        {
                                          key: 'true-audio',
                                          text: '有音频',
                                          value: 'true',
                                        },
                                        {
                                          key: 'false-audio',
                                          text: '无音频',
                                          value: 'false',
                                        },
                                      ]}
                                      value={
                                        rule?.when?.has_audio === undefined
                                          ? 'unset'
                                          : String(rule.when.has_audio)
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          when: {
                                            ...newRules[index].when,
                                            has_audio:
                                              value === 'unset'
                                                ? undefined
                                                : value === 'true',
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={16}>
                                    <Form.Input
                                      label='分辨率'
                                      placeholder='不限制 / 例如 720p、1080p'
                                      value={rule?.when?.resolution || ''}
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        const resolution = value.trim();
                                        newRules[index] = {
                                          ...newRules[index],
                                          when: {
                                            ...newRules[index].when,
                                            resolution:
                                              resolution === ''
                                                ? undefined
                                                : resolution,
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row columns={2}>
                                  <Grid.Column>
                                    <Form.Input
                                      label='覆盖输入价格 USD'
                                      type='number'
                                      step='0.01'
                                      value={
                                        rule?.override?.input_price?.usd || 0
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          override: {
                                            ...newRules[index].override,
                                            input_price: {
                                              ...newRules[index].override
                                                ?.input_price,
                                              usd: parseFloat(value) || 0,
                                            },
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                  <Grid.Column>
                                    <Form.Input
                                      label='覆盖输入价格 CNY'
                                      type='number'
                                      step='0.01'
                                      value={
                                        rule?.override?.input_price?.cny || 0
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          override: {
                                            ...newRules[index].override,
                                            input_price: {
                                              ...newRules[index].override
                                                ?.input_price,
                                              cny: parseFloat(value) || 0,
                                            },
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row columns={2}>
                                  <Grid.Column>
                                    <Form.Input
                                      label='覆盖输出价格 USD'
                                      type='number'
                                      step='0.01'
                                      value={
                                        rule?.override?.output_price?.usd || 0
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          override: {
                                            ...newRules[index].override,
                                            output_price: {
                                              ...newRules[index].override
                                                ?.output_price,
                                              usd: parseFloat(value) || 0,
                                            },
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                  <Grid.Column>
                                    <Form.Input
                                      label='覆盖输出价格 CNY'
                                      type='number'
                                      step='0.01'
                                      value={
                                        rule?.override?.output_price?.cny || 0
                                      }
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          override: {
                                            ...newRules[index].override,
                                            output_price: {
                                              ...newRules[index].override
                                                ?.output_price,
                                              cny: parseFloat(value) || 0,
                                            },
                                          },
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                  <Grid.Column width={16}>
                                    <Form.Input
                                      label='规则说明'
                                      placeholder='例如：生成音频时输出单价翻倍'
                                      value={rule.description || ''}
                                      onChange={(e, { value }) => {
                                        const newRules = [
                                          ...newModel.context_rules,
                                        ];
                                        newRules[index] = {
                                          ...newRules[index],
                                          description: value,
                                        };
                                        setNewModel({
                                          ...newModel,
                                          context_rules: newRules,
                                        });
                                      }}
                                    />
                                  </Grid.Column>
                                </Grid.Row>
                              </Grid>
                            </Segment>
                          ))}
                        </>
                      ) : (
                        <Message info>
                          暂无上下文规则，可按音频/参考视频/分辨率等条件覆盖单价
                        </Message>
                      )}
                      <Button
                        icon='plus'
                        content='添加上下文规则'
                        onClick={() => {
                          setNewModel({
                            ...newModel,
                            context_rules: [
                              ...newModel.context_rules,
                              {
                                when: {},
                                override: {
                                  input_price: { usd: 0, cny: 0 },
                                  output_price: { usd: 0, cny: 0 },
                                },
                                description: '',
                              },
                            ],
                          });
                        }}
                      />
                    </>
                  )}
                </>
              )}

            {!isUpscaleModelType && newModel.type === 'multiplier' && (
              <>
                <Divider horizontal>基础价格</Divider>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='USD'
                    type='number'
                    step='0.01'
                    value={newModel.base_price.usd}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        base_price: {
                          ...newModel.base_price,
                          usd: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                  <Form.Input
                    label='CNY'
                    type='number'
                    step='0.01'
                    value={newModel.base_price.cny}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        base_price: {
                          ...newModel.base_price,
                          cny: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                </Form.Group>
                <Message info size='small'>
                  <p>
                    <strong>倍率配置说明：</strong>
                    <br />
                    分辨率倍率、质量倍率等需要在JSON模式下配置。
                    <br />
                    示例：resolution_ratios:{' '}
                    {`{"1024x1024": 1.0, "1024x1792": 1.5}`}
                  </p>
                </Message>
              </>
            )}

            {!isUpscaleModelType && newModel.type === 'per_char' && (
              <>
                <Divider horizontal>每字符价格</Divider>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='USD'
                    type='number'
                    step='0.000001'
                    value={newModel.price_per_char.usd}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_char: {
                          ...newModel.price_per_char,
                          usd: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                  <Form.Input
                    label='CNY'
                    type='number'
                    step='0.000001'
                    value={newModel.price_per_char.cny}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_char: {
                          ...newModel.price_per_char,
                          cny: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                </Form.Group>
                <Message info size='small'>
                  <p>
                    <strong>质量倍率配置：</strong>
                    如需配置质量倍率（如standard/hd），请在JSON模式下添加。
                  </p>
                </Message>
              </>
            )}

            {!isUpscaleModelType && newModel.type === 'per_request' && (
              <>
                <Divider horizontal>每次请求价格</Divider>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='USD'
                    type='number'
                    step='0.01'
                    value={newModel.price_per_request.usd}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_request: {
                          ...newModel.price_per_request,
                          usd: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                  <Form.Input
                    label='CNY'
                    type='number'
                    step='0.01'
                    value={newModel.price_per_request.cny}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_request: {
                          ...newModel.price_per_request,
                          cny: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                </Form.Group>
              </>
            )}

            {!isUpscaleModelType && newModel.type === 'per_second' && (
              <>
                <Divider horizontal>每秒价格</Divider>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='USD'
                    type='number'
                    step='0.001'
                    value={newModel.price_per_second.usd}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_second: {
                          ...newModel.price_per_second,
                          usd: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                  <Form.Input
                    label='CNY'
                    type='number'
                    step='0.001'
                    value={newModel.price_per_second.cny}
                    onChange={(e, { value }) =>
                      setNewModel({
                        ...newModel,
                        price_per_second: {
                          ...newModel.price_per_second,
                          cny: parseFloat(value) || 0,
                        },
                      })
                    }
                  />
                </Form.Group>
              </>
            )}

            {!isUpscaleModelType && newModel.type === 'conditional' && (
              <>
                <Divider horizontal>条件定价规则</Divider>
                {newModel.conditional_rules.length > 0 ? (
                  <>
                    {newModel.conditional_rules.map((rule, index) => (
                      <Segment key={index} style={{ marginBottom: '10px' }}>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <Label color='blue' ribbon>
                                规则 {index + 1}
                              </Label>
                              <Button
                                floated='right'
                                size='mini'
                                color='red'
                                icon='trash'
                                onClick={() => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules.splice(index, 1);
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输入最小Token数'
                                type='number'
                                value={rule.input_min || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    input_min: parseInt(value) || 0,
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输入最大Token数（-1表示无限）'
                                type='number'
                                value={rule.input_max || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    input_max: parseInt(value) || 0,
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输出最小Token数'
                                type='number'
                                value={rule.output_min || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    output_min: parseInt(value) || 0,
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输出最大Token数（-1表示无限）'
                                type='number'
                                value={rule.output_max || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    output_max: parseInt(value) || 0,
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输入价格 USD'
                                type='number'
                                step='0.01'
                                value={rule.input_price?.usd || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    input_price: {
                                      ...newRules[index].input_price,
                                      usd: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输入价格 CNY'
                                type='number'
                                step='0.01'
                                value={rule.input_price?.cny || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    input_price: {
                                      ...newRules[index].input_price,
                                      cny: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输出价格 USD'
                                type='number'
                                step='0.01'
                                value={rule.output_price?.usd || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    output_price: {
                                      ...newRules[index].output_price,
                                      usd: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='输出价格 CNY'
                                type='number'
                                step='0.01'
                                value={rule.output_price?.cny || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    output_price: {
                                      ...newRules[index].output_price,
                                      cny: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='缓存写入价格 USD'
                                type='number'
                                step='0.000001'
                                value={rule.cache_creation_price?.usd || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    cache_creation_price: {
                                      ...newRules[index].cache_creation_price,
                                      usd: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='缓存写入价格 CNY'
                                type='number'
                                step='0.000001'
                                value={rule.cache_creation_price?.cny || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    cache_creation_price: {
                                      ...newRules[index].cache_creation_price,
                                      cny: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <CacheCreationPricingEditor
                                prices={rule.cache_creation_prices}
                                onChange={(value) => {
                                  const rules = [...newModel.conditional_rules];
                                  rules[index] = { ...rules[index], cache_creation_prices: value };
                                  setNewModel({ ...newModel, conditional_rules: rules });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='缓存读取价格 USD'
                                type='number'
                                step='0.000001'
                                value={rule.cache_read_price?.usd || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    cache_read_price: {
                                      ...newRules[index].cache_read_price,
                                      usd: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                            <Grid.Column width={8}>
                              <Form.Input
                                label='缓存读取价格 CNY'
                                type='number'
                                step='0.000001'
                                value={rule.cache_read_price?.cny || 0}
                                onChange={(e, { value }) => {
                                  const newRules = [
                                    ...newModel.conditional_rules,
                                  ];
                                  newRules[index] = {
                                    ...newRules[index],
                                    cache_read_price: {
                                      ...newRules[index].cache_read_price,
                                      cny: parseFloat(value) || 0,
                                    },
                                  };
                                  setNewModel({
                                    ...newModel,
                                    conditional_rules: newRules,
                                  });
                                }}
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Segment>
                    ))}
                  </>
                ) : (
                  <Message info>暂无规则，请添加至少一个条件定价规则</Message>
                )}
                <Button
                  icon='plus'
                  content='添加规则'
                  onClick={() => {
                    setNewModel({
                      ...newModel,
                      conditional_rules: [
                        ...newModel.conditional_rules,
                        {
                          input_min: 0,
                          input_max: -1,
                          output_min: 0,
                          output_max: -1,
                          input_price: { usd: 0, cny: 0 },
                          output_price: { usd: 0, cny: 0 },
                          cache_creation_price: { usd: 0, cny: 0 },
                          cache_read_price: { usd: 0, cny: 0 },
                        },
                      ],
                    });
                  }}
                />
              </>
            )}

            {!isUpscaleModelType && newModel.type === 'matrix' && (
              <>
                <Divider horizontal>矩阵定价配置</Divider>
                {Object.keys(newModel.price_matrix).length > 0 ? (
                  <>
                    <Table celled compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>维度1</Table.HeaderCell>
                          <Table.HeaderCell>维度2/价格</Table.HeaderCell>
                          <Table.HeaderCell>USD</Table.HeaderCell>
                          <Table.HeaderCell>CNY</Table.HeaderCell>
                          <Table.HeaderCell width={1}>操作</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {Object.entries(newModel.price_matrix).map(
                          ([key1, value]) => {
                            // 检查是否是二维矩阵
                            const isNested =
                              typeof value === 'object' &&
                              !value.usd &&
                              !value.cny;

                            if (isNested) {
                              // 二维矩阵
                              return Object.entries(value).map(
                                ([key2, price], idx) => (
                                  <Table.Row key={`${key1}-${key2}`}>
                                    {idx === 0 && (
                                      <Table.Cell
                                        rowSpan={Object.keys(value).length}
                                      >
                                        <strong>{key1}</strong>
                                      </Table.Cell>
                                    )}
                                    <Table.Cell>{key2}</Table.Cell>
                                    <Table.Cell>
                                      <Form.Input
                                        type='number'
                                        step='0.01'
                                        value={price?.usd || 0}
                                        onChange={(
                                          e,
                                          { value: inputValue }
                                        ) => {
                                          const newMatrix = {
                                            ...newModel.price_matrix,
                                          };
                                          newMatrix[key1][key2] = {
                                            ...newMatrix[key1][key2],
                                            usd: parseFloat(inputValue) || 0,
                                          };
                                          setNewModel({
                                            ...newModel,
                                            price_matrix: newMatrix,
                                          });
                                        }}
                                        style={{ width: '80px' }}
                                      />
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Form.Input
                                        type='number'
                                        step='0.01'
                                        value={price?.cny || 0}
                                        onChange={(
                                          e,
                                          { value: inputValue }
                                        ) => {
                                          const newMatrix = {
                                            ...newModel.price_matrix,
                                          };
                                          newMatrix[key1][key2] = {
                                            ...newMatrix[key1][key2],
                                            cny: parseFloat(inputValue) || 0,
                                          };
                                          setNewModel({
                                            ...newModel,
                                            price_matrix: newMatrix,
                                          });
                                        }}
                                        style={{ width: '80px' }}
                                      />
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Button
                                        size='mini'
                                        icon='trash'
                                        color='red'
                                        onClick={() => {
                                          const newMatrix = {
                                            ...newModel.price_matrix,
                                          };
                                          delete newMatrix[key1][key2];
                                          if (
                                            Object.keys(newMatrix[key1])
                                              .length === 0
                                          ) {
                                            delete newMatrix[key1];
                                          }
                                          setNewModel({
                                            ...newModel,
                                            price_matrix: newMatrix,
                                          });
                                        }}
                                      />
                                    </Table.Cell>
                                  </Table.Row>
                                )
                              );
                            } else {
                              // 一维矩阵
                              return (
                                <Table.Row key={key1}>
                                  <Table.Cell>
                                    <strong>{key1}</strong>
                                  </Table.Cell>
                                  <Table.Cell>-</Table.Cell>
                                  <Table.Cell>
                                    <Form.Input
                                      type='number'
                                      step='0.01'
                                      value={value?.usd || 0}
                                      onChange={(e, { value: inputValue }) => {
                                        const newMatrix = {
                                          ...newModel.price_matrix,
                                        };
                                        newMatrix[key1] = {
                                          ...newMatrix[key1],
                                          usd: parseFloat(inputValue) || 0,
                                        };
                                        setNewModel({
                                          ...newModel,
                                          price_matrix: newMatrix,
                                        });
                                      }}
                                      style={{ width: '80px' }}
                                    />
                                  </Table.Cell>
                                  <Table.Cell>
                                    <Form.Input
                                      type='number'
                                      step='0.01'
                                      value={value?.cny || 0}
                                      onChange={(e, { value: inputValue }) => {
                                        const newMatrix = {
                                          ...newModel.price_matrix,
                                        };
                                        newMatrix[key1] = {
                                          ...newMatrix[key1],
                                          cny: parseFloat(inputValue) || 0,
                                        };
                                        setNewModel({
                                          ...newModel,
                                          price_matrix: newMatrix,
                                        });
                                      }}
                                      style={{ width: '80px' }}
                                    />
                                  </Table.Cell>
                                  <Table.Cell>
                                    <Button
                                      size='mini'
                                      icon='trash'
                                      color='red'
                                      onClick={() => {
                                        const newMatrix = {
                                          ...newModel.price_matrix,
                                        };
                                        delete newMatrix[key1];
                                        setNewModel({
                                          ...newModel,
                                          price_matrix: newMatrix,
                                        });
                                      }}
                                    />
                                  </Table.Cell>
                                </Table.Row>
                              );
                            }
                          }
                        )}
                      </Table.Body>
                    </Table>
                  </>
                ) : (
                  <Message info>
                    暂无矩阵配置，请在JSON编辑模式下配置或添加简单条目
                  </Message>
                )}
                <Message info size='small'>
                  <p>
                    <strong>矩阵定价说明：</strong>
                    <br />
                    支持一维（如分辨率）或二维（如分辨率×质量）矩阵。
                    <br />
                    复杂的矩阵结构建议在JSON编辑模式下配置。
                  </p>
                </Message>
              </>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setShowAddModal(false);
              resetNewModel();
            }}
          >
            取消
          </Button>
          <Button
            primary
            onClick={editingModel ? handleUpdateModel : handleAddModel}
            disabled={!newModel.model}
          >
            {editingModel ? '更新' : '添加'}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default PricingEditor;
